import { ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Bot, X, CheckCircle2, AlertCircle, Clock, DollarSign } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { extractErr } from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type AgentRun = {
  id: number
  agent: number
  agent_name: string
  trigger: string
  triggered_by_username: string | null
  triggered_by_event_code: string
  input_context: Record<string, any>
  output_summary: string
  status: string
  tokens_in: number
  tokens_out: number
  cost_usd: string
  started_at: string | null
  completed_at: string | null
  error: string
  created_at: string
}

type AgentMessage = {
  id: number
  role: 'SYSTEM' | 'USER' | 'ASSISTANT' | 'TOOL'
  content: string
  tool_name: string
  tool_input: any
  tool_output: any
  tokens: number
  timestamp: string
}

const STATUS_COLOR: Record<string, string> = {
  QUEUED: 'bg-gray-100 text-gray-700',
  RUNNING: 'bg-blue-100 text-blue-700',
  WAITING_APPROVAL: 'bg-amber-100 text-amber-800',
  DONE: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-600',
  BUDGET_EXCEEDED: 'bg-red-100 text-red-700',
}

const ROLE_COLOR: Record<AgentMessage['role'], string> = {
  SYSTEM: 'bg-gray-100 text-gray-700',
  USER: 'bg-blue-100 text-blue-800',
  ASSISTANT: 'bg-violet-100 text-violet-700',
  TOOL: 'bg-amber-100 text-amber-800',
}

function relTime(iso: string | null): string {
  if (!iso) return '—'
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return new Date(iso).toLocaleString()
}

// ─────────────────── List page ───────────────────

export function AdminAIAgentRunsList() {
  const navigate = useNavigate()
  const runsQ = useQuery({
    queryKey: ['ai-agents', 'runs'],
    queryFn: async () => (await api.get<AgentRun[]>('/ai-agents/runs/')).data,
    refetchInterval: 5_000,  // tail any RUNNING/QUEUED rows
  })

  const runs = runsQ.data || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Bot className="h-5 w-5 text-muted-foreground" /> Agent runs
          </h2>
          <p className="text-sm text-muted-foreground">
            {runs.length} runs · refreshes every 5s while open.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/ai-agents')}>← Agents</Button>
      </div>

      {runsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : runs.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No runs yet. Open an agent and click <strong>Run</strong> to start one.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>#</Th>
                <Th>Agent</Th>
                <Th>Trigger</Th>
                <Th>Status</Th>
                <Th className="text-right">Tokens</Th>
                <Th className="text-right">Cost</Th>
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/ai-agents/runs/${r.id}`)}
                >
                  <Td className="font-mono text-xs">{r.id}</Td>
                  <Td className="text-sm font-medium">{r.agent_name}</Td>
                  <Td className="text-xs">
                    {r.trigger}
                    {r.triggered_by_username && (
                      <div className="text-muted-foreground">by {r.triggered_by_username}</div>
                    )}
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[r.status] || ''}`}>
                      {r.status}
                    </span>
                  </Td>
                  <Td className="text-right text-xs font-mono">
                    {r.tokens_in.toLocaleString()} / {r.tokens_out.toLocaleString()}
                  </Td>
                  <Td className="text-right text-xs font-mono">${r.cost_usd}</Td>
                  <Td className="text-xs">{relTime(r.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}

// ─────────────────── Detail page ───────────────────

export function AdminAIAgentRunDetail() {
  const { id } = useParams<{ id: string }>()
  const runId = id ? Number(id) : 0
  const qc = useQueryClient()
  const navigate = useNavigate()

  const runQ = useQuery({
    queryKey: ['ai-agents', 'run', runId],
    queryFn: async () =>
      (await api.get<AgentRun & { messages: AgentMessage[] }>(`/ai-agents/runs/${runId}/`)).data,
    enabled: !!runId,
    refetchInterval: (q) => {
      const s = (q.state.data as any)?.status
      return s && ['RUNNING', 'QUEUED', 'WAITING_APPROVAL'].includes(s) ? 3_000 : false
    },
  })

  const cancelMut = useMutation({
    mutationFn: async () => (await api.post(`/ai-agents/runs/${runId}/cancel/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-agents', 'run', runId] })
      toast.success('Run cancelled')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  if (runQ.isLoading || !runQ.data) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (runQ.isError) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Run not found.</p>
          <Link to="/admin/ai-agents/runs" className="text-violet-600 hover:underline">← Back to runs</Link>
        </div>
      </AdminLayout>
    )
  }

  const run = runQ.data
  const messages = run.messages || []
  const stillRunning = ['RUNNING', 'QUEUED', 'WAITING_APPROVAL'].includes(run.status)

  return (
    <AdminLayout>
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/ai-agents/runs" className="text-muted-foreground hover:text-foreground hover:underline">
            ← Runs
          </Link>
        </div>
        <div className="flex items-start justify-between mt-2 gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">
              Run #{run.id} · {run.agent_name}
            </h2>
            <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[run.status] || ''}`}>
                {run.status}
              </span>
              <span><Clock className="h-3 w-3 inline mr-1" /> started {relTime(run.started_at)}</span>
              {run.completed_at && (
                <span>completed {relTime(run.completed_at)}</span>
              )}
              <span><DollarSign className="h-3 w-3 inline mr-1" /> ${run.cost_usd} ({run.tokens_in.toLocaleString()} / {run.tokens_out.toLocaleString()} tokens)</span>
              <span>trigger: {run.trigger}{run.triggered_by_username ? ` (${run.triggered_by_username})` : ''}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {stillRunning && (
              <Button variant="outline" size="sm"
                onClick={() => cancelMut.mutate()} disabled={cancelMut.isPending}>
                <X className="h-3 w-3 mr-1" /> Cancel
              </Button>
            )}
            {run.status === 'WAITING_APPROVAL' && (
              <Button variant="outline" size="sm"
                onClick={() => navigate('/admin/ai-agents/approvals')}>
                Open approval queue →
              </Button>
            )}
          </div>
        </div>

        {run.error && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            <div className="font-semibold mb-1 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" /> Error
            </div>
            <pre className="text-xs whitespace-pre-wrap font-mono">{run.error}</pre>
          </div>
        )}

        {run.status === 'DONE' && run.output_summary && (
          <div className="mt-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
            <div className="font-semibold mb-1 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> Summary
            </div>
            <div className="text-sm whitespace-pre-wrap">{run.output_summary}</div>
          </div>
        )}
      </div>

      {/* Conversation timeline */}
      <div className="bg-card rounded-lg shadow overflow-hidden">
        <div className="px-4 py-2 border-b border-border">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Conversation timeline ({messages.length} messages)
          </h3>
        </div>
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4">No messages yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {messages.map((m) => (
              <li key={m.id} className="px-4 py-3">
                <div className="flex items-start gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${ROLE_COLOR[m.role]}`}>
                    {m.role}
                  </span>
                  <div className="flex-1 min-w-0">
                    {m.tool_name && (
                      <div className="text-xs text-muted-foreground mb-1">
                        Tool: <span className="font-mono">{m.tool_name}</span>
                      </div>
                    )}
                    {m.content && (
                      <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                    )}
                    {m.tool_input && (
                      <details className="mt-1">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                          Tool input
                        </summary>
                        <pre className="text-xs font-mono bg-muted/40 rounded p-2 mt-1 overflow-x-auto">
                          {JSON.stringify(m.tool_input, null, 2)}
                        </pre>
                      </details>
                    )}
                    {m.tool_output && (
                      <details className="mt-1">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                          Tool output
                        </summary>
                        <pre className="text-xs font-mono bg-muted/40 rounded p-2 mt-1 overflow-x-auto">
                          {JSON.stringify(m.tool_output, null, 2)}
                        </pre>
                      </details>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(m.timestamp).toLocaleString()}
                      {m.tokens > 0 && <span> · {m.tokens.toLocaleString()} tokens</span>}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`}>{children}</td>
}
